import * as heroIcons from '@heroicons/react/24/outline';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { DateRangePickerValue, Divider, Text, TextInput } from '@tremor/react';
import _ from 'lodash';
import { FC, ReactElement, SetStateAction, useCallback, useEffect } from 'react';
import { z } from 'zod';
import { ADMIN_USER_PERMISSIONS_TITLE, ADMIN_USER_PERMISSION_SECTIONS, REGEN_FOR_FORMATTED_TITLE } from '../../(dashboards)/admin-users/constants';
import { ANNOUNCEMENT_KEY } from '../../(dashboards)/announcements/constants';
import { ANY_DAY, ANY_LOCATION, ANY_TIME, FROM, TO } from '../../(dashboards)/campaigns/constants';
import { INVOICE_REDEMPTIONS_TAB_KEYS, POINT_TO_DOLLAR_RATE, REAL_VALUE_DECIMAL_POINT } from '../../(dashboards)/invoices/constants';
import { CAMPAIGN_TYPE_FOR_INVITE_TYPE, TARGET_GROUP_TYPE } from '../../(dashboards)/target-groups/constants';
import { SIGN_SCREEN_TYPE_TITLE, TENANT_STEP_TYPES_KEY_NAME } from '../../(dashboards)/tenant-settings/constants';
import { handleValidationErrors } from '../../(dashboards)/tenant-settings/utils';
import { UPDATE_CARD_STATUSES } from '../../(dashboards)/updates/constants';
import { USER_STATUSES } from '../../(dashboards)/users/constants';
import { IMAGE_UPLOAD_SUCCESS, MIGRATION_SUCCESS, NO_VALIDATION_CODE, VERIFY_ACCOUNT_SUCCESS } from '../../(dashboards)/utilities/constants';
import CheckboxInput from '../../components/Checkbox';
import ColorInput from '../../components/ColorInput';
import EditableDateTimePicker from '../../components/EditableDateTimePicker';
import EditableMarkdown from '../../components/EditableMarkdown';
import EditableNumberField from '../../components/EditableNumberField';
import EditableTextField from '../../components/EditableTextField';
import ImagePopOver from '../../components/ImagePopOver';
import TextFieldRO from '../../components/textFieldRO';
import { setError } from '../../redux/slices/errorSlice';
import { CLICK, COMMON_STATUSES, CSV_EXTENSION, DASH, DATE_FORMAT_DETAIL_VIEW, DOWNLOAD, GEO_MAP_TYPE, Languages, MONTH_ONLY_DATE_FORMAT, NO_RECORDS_FOUND_TEXT, NO_RECORDS_NUMBER, PAGINATION_CONSTANTS, POP_UP_TYPES, PREDEFINED_BADGE_ICON_LIST, REQUIRED, UNKNOWN_URL, VALID_EMAIL_REGEX, ZERO_DOLLAR } from '../constants/constants';
import { SearchParams } from '../hooks/useSearchParams';
import dayjs from './dayjsUtils';
import dynamic from 'next/dynamic';
const GeoMap = dynamic(() => import('../../components/GeoMap'), {
  ssr: false
});
export const getStatusProperties = (status?: string, customStatusMap?: {
  [x: string]: {
    borderColor: string;
    bgColor: string;
    color: string;
  };
}): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  const commonStatusMap = {
    [COMMON_STATUSES.LIVE]: {
      borderColor: 'border-green-500',
      bgColor: 'bg-lighter-green',
      color: 'green'
    },
    [COMMON_STATUSES.HIDDEN]: {
      borderColor: 'border-gray-500',
      bgColor: 'bg-lighter-gray',
      color: 'gray'
    },
    [COMMON_STATUSES.EXPIRED]: {
      borderColor: 'border-red-500',
      bgColor: 'bg-lighter-red',
      color: 'red'
    },
    [COMMON_STATUSES.DRAFT]: {
      borderColor: 'border-gray-500',
      bgColor: 'bg-lighter-gray',
      color: 'gray'
    },
    [COMMON_STATUSES.DISABLED]: {
      borderColor: 'border-gray-500',
      bgColor: 'bg-lighter-gray',
      color: 'gray'
    }
  };
  if (status != null) {
    for (const [key, value] of Object.entries(customStatusMap || commonStatusMap)) {
      if (status.includes(key)) {
        return value;
      }
    }
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};
export const getTranslationLang = (langList: string[]): string => {
  return langList.length ? `, ${langList.join(', ')}` : '';
};
export const sanitizeArrayColumn = (value: string | string[]): string => {
  if (typeof value === 'string') {
    return value && value.split(',').join(', ');
  } else {
    return value && value.join(', ');
  }
};
export const handlePageChange = (isNext: boolean, isPrevious: boolean, isPaginationDisabled: boolean, setSearchParams: (params: Partial<SearchParams>) => void, searchParams: SearchParams, pageNumber: number, data?: {
  [key: string]: any;
}): void => {
  if (isPaginationDisabled) {
    return;
  }
  if (isNext) {
    if (data?.result?.pagination && data.result.pagination.pageSize === data.result.items?.length || data?.pagination && data.pagination.pageSize === data.items?.length) {
      setSearchParams({
        ...searchParams,
        pageNumber: (pageNumber + 1).toString()
      });
    }
  }
  if (isPrevious && pageNumber > 1) {
    setSearchParams({
      ...searchParams,
      pageNumber: (pageNumber - 1).toString()
    });
  }
};
export const handleFilterChange = (value: string[] | string, key: string, setSearchParams: (params: Partial<SearchParams>) => void, searchParams: SearchParams): void => {
  setSearchParams({
    ...searchParams,
    [key]: value,
    pageNumber: PAGINATION_CONSTANTS.DEFAULT_CURRENT_PAGE.toString()
  });
};
export const getDateWithFormat = (date: string | Date | undefined | null, format: string): string | Date => {
  return date != null ? dayjs.utc(date).format(format) : '';
};
export const getUserStatusProperties = (status?: string): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  const statusMap = {
    [USER_STATUSES.ACTIVE]: {
      borderColor: 'border-green-500',
      bgColor: 'bg-lighter-green',
      color: 'green'
    },
    [USER_STATUSES.INACTIVE]: {
      borderColor: 'border-gray-500',
      bgColor: 'bg-lighter-gray',
      color: 'gray'
    },
    [USER_STATUSES.SUSPENDED]: {
      borderColor: 'border-red-500',
      bgColor: 'bg-lighter-red',
      color: 'red'
    },
    [USER_STATUSES.NEW]: {
      borderColor: 'border-blue-500',
      bgColor: 'bg-lighter-blue',
      color: 'blue'
    }
  };
  if (status != null) {
    for (const [key, value] of Object.entries(statusMap)) {
      if (status === key) {
        return value;
      }
    }
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};
export const getUtilizedDate = (date: DateRangePickerValue): string => {
  let fromTime = date.from ? dayjs.utc(date.from).startOf('day').toISOString() : null;
  let endTime = date.to ? dayjs.utc(date.to).endOf('day').toISOString() : null;
  if (fromTime && fromTime === endTime) {
    endTime = dayjs.utc(date.from).endOf('day').toISOString();
  }
  return `${fromTime} - ${endTime}`;
};
export const checkIsNotNull = (value: string | null): boolean => value != null && value !== 'null';
export const getTargetGroupType = (campaignType: string, isManual: boolean): string => campaignType === CAMPAIGN_TYPE_FOR_INVITE_TYPE ? TARGET_GROUP_TYPE.INVITE : isManual ? TARGET_GROUP_TYPE.MANUAL : TARGET_GROUP_TYPE.AUTOMATIC;
export const getDateParamsForSearch = (range: string): {
  from: string;
  to: string;
} => {
  const [from, to] = range.split(' - ');
  return {
    from: checkIsNotNull(from) ? from : '',
    to: checkIsNotNull(to) ? to : ''
  };
};
export const handleDateRangeChange = (value: DateRangePickerValue, setValue: (value: SetStateAction<DateRangePickerValue>) => void, key: string, setSearchParams: (params: Partial<SearchParams>) => void, searchParams: SearchParams): void => {
  setValue(value);
  const dateRange = getUtilizedDate(value);
  handleFilterChange(dateRange, key, setSearchParams, searchParams);
};
export const getFormattedTimeForDetailView = (timeStamp: string): string => {
  return dayjs.utc(timeStamp).format(DATE_FORMAT_DETAIL_VIEW);
};
export const renderNoRecordsText = (listLength: number): ReactElement | null => {
  if (listLength < 1) {
    return <>
        <Divider className='my-4' />
        <Text>{NO_RECORDS_FOUND_TEXT}</Text>
      </>;
  }
  return null;
};
export const renderValidationErrorText = (errorMsg: string): ReactElement => {
  return <p className='text-red-500 text-sm mt-2' data-sentry-component="renderValidationErrorText" data-sentry-source-file="utils.tsx">{errorMsg}</p>;
};
export const capitalizeString = (string: string): string => {
  const stringInLowerCase = string.toLowerCase();
  return stringInLowerCase.charAt(0).toUpperCase() + stringInLowerCase.slice(1);
};
export const renderMappedTextFields = (textFieldsArray: TextFieldContent[]) => textFieldsArray.map(fields => <TextFieldRO key={fields.title} value={fields.value || DASH} label={fields.title} attribute={fields.title} isDisabled={fields.isDisabled || false} />);
export const renderMappedEditableTextFields = (textFieldsArray: EditableTextFieldContent[]) => textFieldsArray.map(fields => <EditableTextField key={fields.key} id={fields.key} label={fields.label} changeEvent={fields.changeEvent} value={fields.value} error={fields.error} placeholder={fields.placeholder} maxLength={fields.maxLength} />);
export const renderMappedEditableMarkdown = (textAreaFields: EditableMarkdownContent[]) => textAreaFields.map(fields => <EditableMarkdown key={fields.id} id={fields.id} label={fields.label} changeEvent={fields.changeEvent} value={fields.value} error={fields.error} />);
export const renderMappedEditableNumberFields = (numberFieldsArray: EditableNumberFieldContent[]) => numberFieldsArray.map(fields => {
  const {
    key,
    label,
    changeEvent,
    value,
    error,
    placeholder
  } = fields;
  return <EditableNumberField key={key} id={key} label={label} changeEvent={changeEvent} value={value} error={error} placeholder={placeholder} />;
});
export const renderMappedEditableDatePicker = (fields: EditableDatepickerContent[]) => {
  return fields.map(field => {
    if (field.showField) {
      return <EditableDateTimePicker key={field.id} label={field.label} placeholder={field.placeholder} selectedDateTime={field.selectedDateTime} handleDateChange={field.handleDateChange} error={field.error} id={field.id} minDate={field.minDate} isDisabled={field.isDisabled} description={field.description} />;
    }
  });
};
export const EditableCheckboxWithLabel = (checked: boolean, id: string, handleCheckBoxChange: (checked: boolean, id: string) => void, label: string, className?: string, isDisabled = false) => <div className={`flex items-center space-x-2 ${className}`} data-sentry-component="EditableCheckboxWithLabel" data-sentry-source-file="utils.tsx">
    <CheckboxInput checked={checked} setChecked={handleCheckBoxChange} id={id} isDisabled={isDisabled} data-sentry-element="CheckboxInput" data-sentry-source-file="utils.tsx" />
    <label>{label}</label>
  </div>;
export const renderMappedImgs = (imgs: ImgDisplayContent[]) => imgs.map(img => <div key={img.url}>
      <label htmlFor={img.title}>{img.title}</label>
      {img.url ? <ImagePopOver id={img.title} src={img.url} width={img.width} height={img.height} alt={img.title} className='mt-2' /> : <Text> {DASH}</Text>}
    </div>);
export const getUpdateCardStatusFormatted = (endDate: string, status: string): string => {
  if (status === UPDATE_CARD_STATUSES.DRAFT) {
    return UPDATE_CARD_STATUSES.DRAFT;
  } else {
    if (endDate && dayjs.utc().isAfter(dayjs.utc(endDate))) {
      return UPDATE_CARD_STATUSES.EXPIRED;
    }
    return status;
  }
};
export const renderMappedEditableColorInputs = (themeColorInput: ColorPickerContent[]) => themeColorInput.map(theme => <ColorInput id={theme.key} key={theme.key} isReadOnly={theme.isReadOnly} title={theme.title} color={theme.color} setColor={theme.setColor} colorError={theme.error} changeEvent={theme.changeEvent} />);
export const getPhonValidationCodeProperties = (result?: string): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  if (result != null && result.length > 0) {
    if (result === NO_VALIDATION_CODE) {
      return {
        borderColor: 'border-red-500',
        bgColor: 'bg-lighter-red',
        color: 'red'
      };
    }
    return {
      borderColor: 'border-green-500',
      bgColor: 'bg-lighter-green',
      color: 'green'
    };
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};
export const getLanguageName = (code: string): string => {
  return Languages[code as keyof typeof Languages] || code;
};
export const getMigratePhoneVerificationProperties = (result?: string): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  if (result != null && result.length > 0) {
    if (result === MIGRATION_SUCCESS) {
      return {
        borderColor: 'border-green-500',
        bgColor: 'bg-lighter-green',
        color: 'green'
      };
    }
    return {
      borderColor: 'border-red-500',
      bgColor: 'bg-lighter-red',
      color: 'red'
    };
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};
export const getImageUploadProperties = (result?: string): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  if (result != null && result.length > 0) {
    if (result.includes(IMAGE_UPLOAD_SUCCESS)) {
      return {
        borderColor: 'border-green-500',
        bgColor: 'bg-lighter-green',
        color: 'green'
      };
    }
    return {
      borderColor: 'border-red-500',
      bgColor: 'bg-lighter-red',
      color: 'red'
    };
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};
type HeroIconNames = keyof typeof heroIcons;
export const iconSelection = (): IconSelectionList[] => {
  const list = PREDEFINED_BADGE_ICON_LIST.map((icon: string) => ({
    key: icon,
    value: icon,
    label: IconComponent(icon as HeroIconNames) as unknown as string
  }));
  return list;
};
export const IconComponent = (icon: HeroIconNames | null, className = 'w-5 h-5'): ReactElement | null => {
  const Icon = icon ? heroIcons[icon] as FC<React.SVGProps<SVGSVGElement>> : undefined;
  return Icon ? <Icon className={className} data-sentry-element="Icon" data-sentry-component="IconComponent" data-sentry-source-file="utils.tsx" /> : null;
};
export const removeSpaces = (string: string) => string.replace(/\s/g, '');
export const removeKeysFromObject = <T extends object, K extends keyof T>(arr: T[], keysToRemove: K[]): Omit<T, K>[] => {
  return arr.map(obj => {
    const newObj = {
      ...obj
    };
    keysToRemove.forEach(key => {
      delete newObj[key];
    });
    return newObj;
  });
};
export const utilizeAnnouncementSteps = (announcement: AnnouncementStep[], isFromEdit: boolean): Partial<AnnouncementStep>[] => {
  const deleteKeys = [ANNOUNCEMENT_KEY.IMAGE_FILE as keyof AnnouncementStep, ANNOUNCEMENT_KEY.IMAGE_URL as keyof AnnouncementStep, ANNOUNCEMENT_KEY.STEP as keyof AnnouncementStep];
  if (!isFromEdit) {
    deleteKeys.push(ANNOUNCEMENT_KEY.ANNOUNCEMENT_ID as keyof AnnouncementStep);
  }
  const utilizedAnnouncement = removeKeysFromObject(announcement, deleteKeys);
  return utilizedAnnouncement.map((step: Partial<AnnouncementStep>) => {
    const targetStep = step;
    Object.keys(targetStep).forEach(key => {
      if (targetStep[key as keyof AnnouncementStep] === '') {
        targetStep[key as keyof AnnouncementStep] = null;
      }
    });
    return targetStep;
  }) as Partial<AnnouncementStep>[];
};
export const isCsvExtension = (file: File | null): boolean => (file?.name?.split('.').pop() || '').toLowerCase() === CSV_EXTENSION;
export const csvToArray = async (csv: File, withHeaders: boolean): Promise<string[]> => {
  const fileContent = (await csv.text()).replace(/\r/g, '');
  if (withHeaders) {
    return fileContent.split('\n').filter(line => line.length > 0);
  } else {
    return fileContent.split('\n').slice(1).filter(line => line.length > 0);
  }
};
export const convertToUSD = (points: number | string): string | number => {
  if (typeof points === 'string') {
    points = parseFloat(points.replace(/,/g, ''));
  }
  const usd = points / POINT_TO_DOLLAR_RATE;
  return formattedNumber(usd.toFixed(REAL_VALUE_DECIMAL_POINT));
};
export const arrayToCSV = (data: Record<string, any>[]): string => {
  if (data.length === 0) return '';
  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(','), ...data.map(row => headers.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))].join('\n');
  return csvRows;
};
const replacer = (key: string, value: any) => value === undefined ? '' : value;
export const formattedNumber = (value: number | string): string | number => {
  return value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : value;
};
export const getTotalNumber = (result: any[], keyName: string): number => {
  const total = result.reduce((sum, offer) => {
    const value = offer[keyName];
    if (keyName !== INVOICE_REDEMPTIONS_TAB_KEYS.REDEMPTIONS) {
      return sum + parseFloat(value) || 0;
    } else {
      return sum + (parseInt(value) || 0);
    }
  }, 0);
  return keyName !== INVOICE_REDEMPTIONS_TAB_KEYS.REDEMPTIONS ? formattedNumber(total.toFixed(2)) as number : total;
};
export const validateCsvFileHeaders = async (csv: File, fields: string[]): Promise<boolean> => {
  const fileContent = (await csv.text()).replace(/\r/g, '');
  if (!fileContent.trim()) {
    return false;
  }
  const headers = fileContent.split('\n', 1)[0].replaceAll('"', '').split(',').map(header => header.trim().toLowerCase());
  if (headers.length === 0 || headers.every(header => !header)) {
    return false;
  }
  const mismatchedFields = fields.filter(field => !headers.includes(field?.toLowerCase()));
  if (headers.length !== fields.length || mismatchedFields.length > NO_RECORDS_NUMBER) {
    return false;
  }
  return true;
};
export const validateCsvIsNotEmpty = async (csv: File): Promise<boolean> => {
  const fileContent = (await csv.text()).replace(/\r/g, '');
  const rows = fileContent.split('\n').filter(line => line.length > 0);
  return rows.length > 1 ? true : false;
};
export const cleanCsvContent = async (csv: File): Promise<string> => {
  const fileContent = (await csv.text()).replace(/\r/g, '');
  return fileContent.split('\n').map(line => line.trim().replace(/,+$/, '')).join('\n');
};
export const formatCurrency = (value: number | undefined | null) => {
  if (value === null || value === undefined) return DASH;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};
export const removeDuplicateValuesFromStringObject = (input: Record<string, string>): Record<string, string> => {
  const uniqueValues: Record<string, boolean> = {};
  const result: Record<string, string> = {};
  for (const key in input) {
    const value = input[key];
    if (!uniqueValues[value]) {
      uniqueValues[value] = true;
      result[key] = value;
    }
  }
  return result;
};
export const downloadS3File = async (s3Link: string) => {
  try {
    const response = await fetch(s3Link);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = new URL(s3Link).pathname.split('/').pop() || DOWNLOAD;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};
export const getDateRangeFromMonth = (date: Date | null | string): MonthPickerValue => {
  const startOfMonth = dayjs.utc(date).toISOString();
  const endOfMonth = dayjs.utc(startOfMonth).endOf('month').toISOString();
  return {
    from: startOfMonth,
    to: endOfMonth,
    range: `${startOfMonth} - ${endOfMonth}`
  };
};
export const getMonthByStartDate = (date: string | Date | undefined): string | Date => {
  return date != null ? dayjs.utc(date).format(MONTH_ONLY_DATE_FORMAT) : '';
};
export const checkSectionPermissionByKeyName = (permissions: AdminUserPermissions | undefined, sectionName: string, keyName: string): boolean => {
  if (permissions) {
    if (sectionName === ADMIN_USER_PERMISSION_SECTIONS.PUBLIC_SECTION) {
      return true;
    }
    const permissionSection = permissions[sectionName as keyof AdminUserPermissions];
    if (permissionSection && keyName in permissionSection) {
      return permissionSection[keyName as keyof AdminUserPermissionsType];
    }
  }
  return false;
};
export const capitalizeWithAutoSpacing = (value: string) => {
  return value.split(/(?=[A-Z])/).join(' ').replace(/\b\w/g, char => char.toUpperCase());
};
export const isValidJson = (value?: string | null): boolean => {
  if (value) {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null;
    } catch (error) {
      return false;
    }
  }
  return false;
};
export const getTranslationValue = (translationData: TranslationFields | null, field: string): string | null => {
  return translationData ? translationData[field as keyof TranslationFields] : null;
};
const formattedPermissionTitle = (sectionName: string, permission: string): string | undefined => {
  const sectionData = ADMIN_USER_PERMISSIONS_TITLE[sectionName];
  if (sectionData) {
    return sectionData[permission];
  }
  return capitalizeWithAutoSpacing(permission);
};
export const getPermissionTitle = (sectionName: string, actionsPermitted: string[]) => {
  return actionsPermitted.map(action => formattedPermissionTitle(sectionName, action)).join(', ');
};
export const getFormattedSectionName = (sectionName: string): string => {
  return sectionName.replace(REGEN_FOR_FORMATTED_TITLE, '$1 $2').replace(/Section$/, '').trim();
};
export const getFilterPermissions = (updatePermissions: AdminUserPermissions): Partial<AdminUserPermissions> => {
  const filteredPermissions: Partial<AdminUserPermissions> = {};
  Object.keys(updatePermissions).forEach(targetPermission => {
    const section = updatePermissions[targetPermission as keyof AdminUserPermissions];
    const permissionsWithFalse: Partial<typeof section> = {};
    Object.keys(section).forEach(key => {
      if (section[key as keyof AdminUserPermissionsType] === false) {
        permissionsWithFalse[key as keyof typeof section] = section[key as keyof typeof section];
      }
    });
    if (Object.keys(permissionsWithFalse).length === 0) {
      filteredPermissions[targetPermission as keyof AdminUserPermissions] = {} as typeof section;
    } else {
      filteredPermissions[targetPermission as keyof AdminUserPermissions] = permissionsWithFalse as typeof section;
    }
  });
  return filteredPermissions;
};
export const getTargetTranslations = (selectedLang: string, translations: TranslationFields | TranslationFields[], isArrayType?: boolean): TranslationFields | TranslationFields[] => {
  if (isArrayType && Array.isArray(translations)) {
    return translations.filter(translation => translation['LanguageCode' as keyof TranslationFields] === selectedLang) as TranslationFields[];
  }
  return Object(translations).find((translation: TranslationFields) => translation['LanguageCode' as keyof TranslationFields] === selectedLang) as TranslationFields;
};
export const modifiedBadgeTranslations = (badgeList: TranslationBadge[], badgeTranslations: TranslationBadge[] | null | undefined | [], selectedLanguage: string): TranslationBadge[] => {
  if (!badgeTranslations || badgeTranslations.length === 0) {
    return badgeList.map(badge => ({
      UpdateCardBadgeTranslationID: badge.UpdateCardBadgeTranslationID ?? null,
      BadgeLabel: null,
      UiPosition: badge.UiPosition,
      LanguageCode: selectedLanguage
    }));
  }
  const updatedBadgeTranslations = [...badgeTranslations];
  badgeList.forEach(badge => {
    const existingBadge = updatedBadgeTranslations.find(item => item.UiPosition === badge.UiPosition);
    if (!existingBadge) {
      updatedBadgeTranslations.push({
        BadgeLabel: null,
        UiPosition: badge.UiPosition,
        LanguageCode: selectedLanguage
      });
    }
  });
  return updatedBadgeTranslations.sort((a, b) => a.UiPosition - b.UiPosition);
};
export const getEditTranslationsBTNClassNames = (hasEditTranslationPermission: boolean, selectedLanguage: string): string => {
  return `${!hasEditTranslationPermission || selectedLanguage === '' ? 'opacity-50 cursor-not-allowed' : ''}cursor-pointer block px-4 py-2 text-gray-700 text-sm hover:bg-gray-100 w-full text-left`;
};
export const getTargetLanguageDataByTargetID = (languageData: TranslationFields[], targetID: string | number, idKeyName: string): TranslationFields | null => {
  const tragetTransltaion = languageData.find(data => {
    return data[idKeyName as keyof TranslationFields] === targetID;
  });
  return tragetTransltaion ?? null;
};
export const getStepTitle = (type: 'signIn' | 'signUp', stepPosition: string | number, field: 'StepTitle' | 'StepDescription'): string => {
  return `${type === 'signIn' ? 'Sign In' : 'Sign Up'} Screen - Step ${stepPosition} ${field === 'StepTitle' ? 'Title' : 'Description'}`;
};
export const getTutorialStepTitle = (step: number, field: 'Title' | 'Description'): string => {
  return `Tutorial Step ${step} - ${field}`;
};
export const getTenantSectionByName = (sectionName: string, translations: TenantTranslationSections): Record<string, string> | null => {
  for (const [key, value] of Object.entries(translations)) {
    const foundEntry = Object.entries(value as Record<string, string>).find(([k]) => k === sectionName);
    if (foundEntry) {
      return {
        [foundEntry[0]]: foundEntry[1] as string
      };
    }
  }
  return null;
};
export const getDefaultOrTranslationKeyName = (languageData: TranslationFields | null, targetField: string): string | undefined => {
  return languageData?.[targetField as keyof TranslationFields];
};
export const getTenantStepFieldName = (isTutorial: boolean, type: string, stepNumber: number | string, fieldType: string) => {
  if (isTutorial) {
    return `Tutorial Step ${stepNumber} - ${fieldType}`;
  }
  if (type === TENANT_STEP_TYPES_KEY_NAME.REFERRAL) {
    return `How does it Work - Step ${stepNumber}`;
  }
  const signStepType = type === TENANT_STEP_TYPES_KEY_NAME.SIGN_UP ? SIGN_SCREEN_TYPE_TITLE.SIGN_UP : SIGN_SCREEN_TYPE_TITLE.SIGN_IN;
  return `${signStepType} - Step ${stepNumber} ${fieldType}`;
};
export const useClickOutsideMenu = (ref: React.RefObject<HTMLElement>, callback: () => void, ignoreSelectors: string[] | undefined = []): void => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement;
      if (ignoreSelectors.some(selector => target.closest(selector))) {
        return;
      }
      if (ref.current && !ref.current.contains(target)) {
        callback();
      }
    };
    document.addEventListener(CLICK, handleClickOutside);
    return () => {
      document.removeEventListener(CLICK, handleClickOutside);
    };
  }, [ref, callback, ignoreSelectors]);
};
export const captureSentryError = (error: CustomError): void => {
  try {
    let err: Error;
    const requestUrl = error?.meta?.request?.url || UNKNOWN_URL;
    if (error instanceof Error) {
      err = error;
    } else if (typeof error === 'object' && error != null) {
      const errorObj = error as CustomError;
      let message = errorObj.message || typeof errorObj.error === 'string' && errorObj.error || typeof errorObj.error === 'object' && errorObj.error?.data?.message || typeof errorObj.error === 'object' && errorObj.error?.message || errorObj.data?.message || errorObj.data?.error;
      if (!message || typeof message !== 'string') {
        const status = errorObj.status || errorObj.data?.status || typeof errorObj.error === 'object' && errorObj.error?.status || 500;
        message = `API Error${status ? ` ${status}` : ''} at ${requestUrl}`;
      }
      err = new Error(message, {
        cause: errorObj as unknown
      });
    } else {
      err = new Error(String(error));
    }
    Sentry.withScope(scope => {
      scope.setExtras({
        message: err.message,
        stack: err.stack,
        requestUrl
      });
      Sentry.captureException(err);
    });
  } catch (captureError: any) {
    try {
      const fallbackError = new Error(`Error capture failed: ${captureError?.message || 'Unknown error'}`);
      Sentry.withScope(scope => {
        scope.setExtras({
          message: fallbackError.message,
          captureError: captureError?.message,
          requestUrl: error?.meta?.request?.url || UNKNOWN_URL
        });
        Sentry.captureException(fallbackError);
      });
    } catch {
      console.error('Complete Sentry capture failure. Original error:', error);
    }
  }
};
export const handleError = (error: CustomError, dispatch: Dispatch<AnyAction>): void => {
  captureSentryError(error);
  dispatch(setError());
};
export const isValidURL = (url?: string): boolean => {
  try {
    if (!url) return false;
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};
export const addCypressSelector = (attribute: string) => {
  return process.env.NEXT_PUBLIC_CYPRESS_SHOW_TAGS_FLAG === 'true' ? {
    'data-cy': attribute
  } : {};
};
export const handleOpenPopup = (type: PopupType, setPopupType: (value: SetStateAction<PopupType>) => void): void => {
  setPopupType(type);
};
export const useUpdateFormImage = <T extends BaseCampaign | BaseOffer | BaseUpdateCard | BaseBusinessUser | BaseProgram,>({
  setImages,
  setErrors,
  resetErrorKey,
  targetType
}: {
  setImages: (value: SetStateAction<CommonImages>) => void;
  setErrors: (value: SetStateAction<Partial<T>>) => void;
  resetErrorKey: string;
  targetType: string;
}): ((type: CommonImgTypes, file: File | null) => void) => {
  return useCallback((type: CommonImgTypes, file: File | null) => {
    setImages(prevImages => ({
      ...prevImages,
      [type]: file
    }));
    if (type === targetType) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [resetErrorKey]: ''
      }));
    }
  }, [setImages, setErrors, resetErrorKey, targetType]);
};
export const uploadImageIfExists = async <T extends BaseCampaign | BaseOffer | BaseUpdateCard | BasePointsDistribution | BaseInviteCodeSegment | TenantReferralCampaign | BaseActionBox | BaseBusinessUser | BaseProgram, K extends keyof T>(imageFile: File | null, key: K, uploadImage: (file: File) => Promise<{
  success: boolean;
  url?: string;
  imageID?: string;
  error?: string;
}>, updatedEntity: T): Promise<void> => {
  if (imageFile) {
    const uploadedImage = await uploadImage(imageFile);
    if (uploadedImage.error) {
      throw new Error(uploadedImage.error);
    } else {
      updatedEntity[key] = uploadedImage.imageID as T[K];
    }
  }
};
export const handlePrevious = (activeTab: number, setActiveTab: (value: SetStateAction<number>) => void): void => {
  setActiveTab(activeTab - 1);
};
export const handleValidationResult = (validationResponse: CommonValidationResponse, setErrors: (errors: Record<string, string>) => void): boolean => {
  if (!validationResponse?.result.success) {
    handleValidationErrors(validationResponse, setErrors);
    return false;
  }
  return true;
};
export const useUpdateFeaturedCampaigns = (existingFeaturedCampaigns: CampaignPositionInCategory[], setState: React.Dispatch<SetStateAction<BaseCampaign>>): void => {
  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      updatedFeaturedUIs: existingFeaturedCampaigns
    }));
  }, [existingFeaturedCampaigns, setState]);
};
export const useValidateImage = <T extends BaseCampaign | BaseOffer | BaseUpdateCard | BaseBusinessUser | BaseProgram,>(isInvalidImage: boolean, imageKey: string, setErrors: React.Dispatch<React.SetStateAction<Partial<T>>>): (() => boolean) => {
  return useCallback((): boolean => {
    if (isInvalidImage) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [imageKey]: REQUIRED
      }));
      return false;
    }
    return true;
  }, [setErrors, imageKey, isInvalidImage]);
};
export const useHandleNextStep = (validateInfo: () => Promise<boolean>, isLast: boolean, setActiveTab: React.Dispatch<React.SetStateAction<number>>, handleOpenPopup: (type: PopupType, setPopupType: React.Dispatch<React.SetStateAction<PopupType>>) => void, setPopupType: React.Dispatch<React.SetStateAction<PopupType>>, dispatch: Dispatch<AnyAction>, activeTab: number, type: PopupType, validateImgs?: () => boolean): (() => Promise<void>) => {
  return useCallback(async (): Promise<void> => {
    try {
      const stepValid = await validateInfo();
      const imgValid = validateImgs ? validateImgs() : true;
      if (stepValid && imgValid) {
        if (isLast) {
          handleOpenPopup(type, setPopupType);
        } else {
          setActiveTab(activeTab + 1);
        }
      }
    } catch (error: any) {
      handleError(error, dispatch);
    }
  }, [dispatch, activeTab, setActiveTab, isLast, validateImgs, validateInfo, handleOpenPopup, setPopupType, type]);
};
export const useHandleSaveAsDraft = (validateInfo: (IsDraft?: boolean) => Promise<boolean>, handleOpenPopup: (type: PopupType, setPopupType: React.Dispatch<React.SetStateAction<PopupType>>) => void, setPopupType: React.Dispatch<React.SetStateAction<PopupType>>, dispatch: Dispatch<AnyAction>, flag?: boolean, validateImgs?: () => boolean): (() => Promise<void>) => {
  return useCallback(async (): Promise<void> => {
    try {
      const validateStep = await validateInfo(flag);
      const validateImg = validateImgs?.() || true;
      if (validateStep && validateImg) {
        handleOpenPopup(POP_UP_TYPES.DRAFT, setPopupType);
      }
    } catch (error: any) {
      handleError(error, dispatch);
    }
  }, [dispatch, validateImgs, validateInfo, handleOpenPopup, setPopupType, flag]);
};
export const isValidEmail = (email: string): boolean => {
  return VALID_EMAIL_REGEX.test(email);
};
export const renderValidDays = (specificDays: string[]): JSX.Element => {
  const validDays = !_.isEmpty(specificDays) ? specificDays.join(', ') : ANY_DAY;
  return <TextFieldRO label='Valid Days' attribute='valid-days' value={validDays} data-sentry-element="TextFieldRO" data-sentry-component="renderValidDays" data-sentry-source-file="utils.tsx" />;
};
export const renderValidTimes = (specificTimes: CampaignSpecificTime[]): JSX.Element => {
  let content;
  if (specificTimes && specificTimes.length > 0) {
    let innerContent = _.map(specificTimes, (specificTimeInput, index) => {
      return <div key={`${index}-specific-times`}>
          {FROM + specificTimeInput.startTime + TO + specificTimeInput.endTime}
        </div>;
    });
    content = <div id='valid-times' className='custom-text-area border border-gray-200 p-2 rounded-md bg-white cursor-not-allowed'>
        {innerContent}
      </div>;
  } else {
    content = <TextInput id='valid-times' value={ANY_TIME} readOnly className='custom-text-input' />;
  }
  return <div data-sentry-component="renderValidTimes" data-sentry-source-file="utils.tsx">
      <label htmlFor='valid-times'>Valid Times</label>
      {content}
    </div>;
};
export const renderValidLocations = (campaignGeoInfo: CampaignGeoInfo[]): JSX.Element => {
  let content;
  if (!_.isEmpty(campaignGeoInfo)) {
    content = renderGeoJsonLocation(campaignGeoInfo);
  } else {
    content = <TextInput id='valid-locations' value={ANY_LOCATION} readOnly className='custom-text-input' />;
  }
  return <div data-sentry-component="renderValidLocations" data-sentry-source-file="utils.tsx">
      <label htmlFor='valid-locations'>Valid Locations</label>
      {content}
    </div>;
};
export const renderGeoJsonLocation = (campaignGeoInfo: CampaignGeoInfo[]): JSX.Element[] => {
  const content = _.map(campaignGeoInfo, (info, index) => {
    return <>
        <div key={`${index}-geo-info`} className='flex gap-3 items-center mb-1'>
          <TextInput id='description' value={`${info.Description || DASH}`} readOnly className='custom-text-input flex-1' />
          <TextInput id='start-location' value={`${info.StartLocationGeoJson || DASH}`} readOnly className='custom-text-input flex-1' />
          <TextInput id='relation' value={`${info.StartEndRelation || DASH}`} readOnly className='custom-text-input flex-[0.3] min-w-0' />
          <TextInput id='end-location' value={`${info.EndLocationGeoJson || DASH}`} readOnly className='custom-text-input flex-1' />
        </div>
        <div className='geo-map-container'>
          <GeoMap geoInfo={info} index={index} />
        </div>
      </>;
  });
  return content;
};
export const isValidGeoJsonFeatureCollection = (json: any): boolean => {
  if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
    return false;
  }
  return json.features.every((feature: any) => feature.type === 'Feature' && feature.geometry && GEO_MAP_TYPE.includes(feature.geometry.type) && Array.isArray(feature.geometry.coordinates));
};
const uuid = z.uuid();
const number = z.string().regex(/^\d+$/);
export function isValidUUID(param: string | undefined | null): boolean {
  return uuid.safeParse(param).success;
}
export function isValidNumber(param: string | undefined | null): boolean {
  return number.safeParse(param).success;
}
export const getPaginationData = (data?: {
  result?: {
    totalCount?: number;
    currentPageRange?: {
      start?: number;
      end?: number;
    };
  };
  pagination?: {
    totalItems?: number;
    itemsIndex?: {
      start?: number;
      end?: number;
    };
  };
}): {
  totalCount: number;
  currentPageStartIndex: number;
  currentPageEndIndex: number;
} => {
  const restPagination = data?.pagination;
  const rpcPagination = data?.result;
  return {
    totalCount: restPagination?.totalItems ?? rpcPagination?.totalCount ?? 0,
    currentPageStartIndex: restPagination?.itemsIndex?.start ?? rpcPagination?.currentPageRange?.start ?? 0,
    currentPageEndIndex: restPagination?.itemsIndex?.end ?? rpcPagination?.currentPageRange?.end ?? 0
  };
};
export const getCategoryIdFieldName = (categoryType: string): string => {
  switch (categoryType) {
    case 'Action Box Category':
      return 'ActionBoxCategoryID';
    case 'Offer Category':
      return 'OfferCategoryID';
    case 'Campaign Category':
      return 'CampaignCategoryID';
    default:
      return 'CategoryID';
  }
};
export const getCategoryRoutePath = (categoryType: string, categoryID: string | number): string => {
  switch (categoryType) {
    case 'Action Box Category':
      return `/action-box-categories/${categoryID}`;
    case 'Offer Category':
      return `/offer-categories/${categoryID}`;
    case 'Campaign Category':
      return `/campaign-categories/${categoryID}`;
    default:
      return `/categories/${categoryID}`;
  }
};
export const formatToDollar = (amount: number | string): string => {
  const num = Number(amount);
  if (isNaN(num)) return ZERO_DOLLAR;
  return `$${num.toFixed(2)}`;
};
export const getVerifyAccountProperties = (result?: string): {
  borderColor: string;
  bgColor: string;
  color: string;
} => {
  if (result != null && result.length > 0) {
    if (result === VERIFY_ACCOUNT_SUCCESS) {
      return {
        borderColor: 'border-green-500',
        bgColor: 'bg-lighter-green',
        color: 'green'
      };
    }
    return {
      borderColor: 'border-red-500',
      bgColor: 'bg-lighter-red',
      color: 'red'
    };
  }
  return {
    borderColor: '',
    bgColor: '',
    color: 'transparent'
  };
};